import { Box, Modal, Button, Group, Grid, Text, Title, TextInput, Container, Card, LoadingOverlay, Badge, Center } from '@mantine/core';
import { IconPlus, IconTrash, IconEdit, IconCrown } from '@tabler/icons-react';
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from 'react-router-dom';
import { ConsentService } from "../../services/consentService";
import { useDisclosure } from '@mantine/hooks';
import { showNotification } from '@mantine/notifications';
import { useForm } from '@mantine/form';
import { MantineReactTable, type MRT_ColumnDef } from 'mantine-react-table';
import { ConfigContext } from '../../Context/configContext';
import { useTitle } from '../hooks/useTitle';
import { themesConfig, defaultBannerContent } from '@/utils/utils';
import { Wizard } from './consentV2/Wizard';
import noDataImage from '../../images/no-domain.jpg';
import { useTranslation } from 'react-i18next';

const tempObj = { language: 'en', domainId: '', domainUrl: '', geoLocations: [], consentExpire: '', styles: '', locations: [] };

// Define the data type for each row
type DomainData = {
    domain: string;
    consentExpire: string;
    category: string;
    status: string;
    domainId:string;
};

function DomainPage(props:any): JSX.Element {
    const { config } = useContext(ConfigContext);
    const { t } = useTranslation();
    const [data, setData] = useState<DomainData[]>([]);
    const [opened, { close, open }] = useDisclosure(false);
    const [deleteDomainId, setDeleteDomainId] = useState<any | null>(null);
    const [siteOpened, setOpened] = useState(false);
    const urlRegex = new RegExp('(https?://)?([\\da-z.-]+)\\.([a-z.]{2,6})[/\\w .-]*/?');
    const [consentConfigRequest] = useState<any>(tempObj);
    const [loading, setLoading] = useState(false);
    const consentService = useMemo(() => new ConsentService(config!), [config]);
    const [consentExpiry, setConsentExpiry] = useState<any>('6 months');
    const [bannerLayout, setBannerLayout] = useState<any>('Box');
    const [bannerPosition, setBannerPosition] = useState<any>('leftBottomPopUp');
    const [showWizard, setShowWizard] = useState(false); // Controls Wizard visibility
    const navigate = useNavigate();

    useTitle(t('domain.title'));

    const form = useForm({
        initialValues: { url: '' },
        validate: {
            url: (value) => !value ? t('validation.required', { field: 'Domain URL' }) : (urlRegex.test(value) ? null : t('validation.invalidDomainUrl')),
        },
    });

    const getTableData = useCallback(async () => {
        setLoading(true);
        try {
            const response = await consentService.getDomains();
            console.log("API Response:", response); // Log the response
            setData(response.data);
        } catch (error) {
            console.error("Error fetching domains:", error);
        } finally {
            setLoading(false);
        }
    }, [consentService]);

    useEffect(() => {
        getTableData();
    }, [getTableData]);

    const handleRefresh = (domainID:any) => {
        getTableData();
        localStorage.setItem("newUser", "false");
        props.onDomainAdded();
    };

    const deleteDomainModal = useCallback((domainId: string) => {
        open();
        setDeleteDomainId(domainId);
    }, [open]);

    const deleteDomain = async () => {
        if (deleteDomainId) {
            try {
                await consentService.deleteDomain(deleteDomainId);
                showNotification({
                    title: 'Success',
                    message: t('domain.deleteSuccess'),
                    color: 'green',
                });
                setDeleteDomainId(null);
                getTableData();
                close();
            } catch (error) {
                showNotification({
                    title: 'Error',
                    message: t('domain.deleteError'),
                    color: 'red',
                });
            }
        }
    };

    const columns = useMemo<MRT_ColumnDef<DomainData>[]>(() => [
        { accessorKey: 'domain', header: t('domain.column.domain') },
        { accessorKey: 'consentExpire', header: t('domain.column.consentExpire') },
        { accessorKey: 'category', header: t('domain.column.category') },
        { 
            accessorKey: 'status', 
            header: t('domain.column.status'),
            Cell: ({ row }) => (
                <Badge variant="light" color={row.original.status === "Active" ? 'green':'orange'}>
                    <Text tt="capitalize" size="xs" fw={700}>
                        {row.original.status}
                    </Text>
                </Badge>
            ),
        },
        {
            header: t('domain.column.actions'),
            Cell: ({ row }) => (
                <Group>
                    <Link to={`/pricing?domainId=${row.original.domainId}`} onClick={(e) => {
                        e.stopPropagation(); 
                    }}>
                    <Text size="xs" c="blue">
                        <IconCrown stroke={1.3} />
                    </Text>
                    </Link>
                    <Link to="/consentBanners" onClick={(e) => {
                        e.stopPropagation(); 
                        localStorage.setItem("activeDomain", row.original.domain);
                        localStorage.setItem("activeDomainId", row.original.domainId);
                    }}>
                        <Text c="green" fw="700" style={{ cursor: 'pointer' }}>
                            <IconEdit size={22} />
                        </Text>
                    </Link>
                    <Text style={{ cursor: 'pointer' }} c="orange" fw="700" onClick={(e) => {  e.stopPropagation(); deleteDomainModal(row.original.domainId) }}>
                        <IconTrash size={22} />
                    </Text>
                </Group>
            ),
        }
    ],
    [deleteDomainModal],
    );

    return (
        <React.Fragment>
            {loading && (
                <LoadingOverlay visible={true} zIndex={1000}   overlayProps={{ radius: 'sm', blur: 2 }} loaderProps={{ color: 'green', type: 'bars' }}/>
            )}
            <>
                <Container fluid>
                    <Card withBorder shadow="sm" radius="md" mb="sm">
                        <Grid>
                            <Grid.Col span={{ base: 12, md: 6, lg: 10 }}>
                                <Title order={2} mb="sm">{t('domain.title')}</Title>
                                <Text size="sm">
                                    {t('domain.description')}
                                </Text>
                            </Grid.Col>
                            <Grid.Col span={{ base: 12, md: 6, lg: 2 }}>
                                <Button onClick={() => setShowWizard(true)} mt="md">
                                    <IconPlus size={18} /> &nbsp; {t('domain.addNewSite')}
                                </Button>
                            </Grid.Col>
                        </Grid>
                    </Card>
                    <Box>
                        {data.length > 0 ? (
                            <MantineReactTable
                                columns={columns}
                                data={data}
                                state={{ isLoading: loading }}
                                enablePagination
                                enableColumnActions={false}
                                initialState={{
                                    showGlobalFilter: true
                                }}  
                                renderToolbarInternalActions={() => {return <><div></div></>}}  
                                mantineSearchTextInputProps={{
                                    placeholder: t('domain.search'),
                                    style: { minWidth: '18rem', display:'block'},
                                    variant: 'filled',
                                }}               
                                mantineTableHeadCellProps={{
                                    style: {
                                      backgroundColor: '#f9f9f9', // Changed header background color
                                      color: '#000',               // Changed header text color
                                      fontWeight: 600,
                                      borderBottom: '2px solid #0056b3', // Darker border for header
                                      padding: '12px',             // Added padding for header cells
                                    },
                                  }}
                                  mantineTableBodyCellProps={{
                                    style: {
                                      padding: '12px',
                                      borderBottom: '1px solid #eee',
                                      transition: 'background-color 0.3s', // Smooth transition for hover effect
                                    },
                                  }}
                                  mantineTableBodyRowProps={({ row }) => ({
                                    onClick: () => {
                                        // Save the clicked domain details in localStorage
                                        localStorage.setItem("activeDomain", row.original.domain);
                                        localStorage.setItem("activeDomainId", row.original.domainId);
                                        // Redirect to the desired page
                                        navigate("/privacy-audits", { replace: true });
                                    },
                                    style: {
                                        cursor: 'pointer', // Change cursor to pointer on row hover
                                    },
                                  })}
                            />
                         ) : (
                            <Card>
                              <Center 
                                  style={{ 
                                    flexDirection: 'column',
                                    gap: '24px',
                                    padding: '60px 20px',
                                    backgroundColor: '#f8f9fa',
                                    borderRadius: '8px',
                                    width: '100%',
                                    minHeight: '300px'
                                  }}
                                >
                                  <img 
                                    src={noDataImage} 
                                    alt="No Data Available"
                                    style={{ 
                                      width: '280px',
                                      height: 'auto',
                                      opacity: 0.8,
                                      filter: 'grayscale(20%)',
                                      marginBottom: '8px'
                                    }} 
                                  />
                                  <div style={{ textAlign: 'center' }}>
                                    <Text size="xl" fw={500} mb={8}>{t('domain.emptyListTitle')}</Text>
                                    <Text c="dimmed" size="sm">
                                      {t('domain.emptyListDescription')}
                                    </Text>
                                    
                                  </div>
                              </Center>
                            </Card>
                          )}
                    </Box>
                    <Modal opened={opened} onClose={close} size="sm" title={t('domain.deleteModal.title')}>
                        <Text ta="center">{t('domain.deleteModal.message')}</Text>
                        <Group mt="xl" justify="center">
                            <Button onClick={close} variant="outline">{t('common.no')}</Button>
                            <Button onClick={deleteDomain} variant="outline" color="red">{t('common.yesDelete')}</Button>
                        </Group>
                    </Modal>
                    {showWizard && (
                        <Wizard handleRefresh={handleRefresh} />
                    )}
                </Container>
            </>
        </React.Fragment>
    );
}

export { DomainPage };
